// Layer 3 (Transformations): the Superintendent "14-shift rolling heat map" —
// map-role-dashboards.md §6a. Rows are the last 14 *completed* shifts of the
// seed series at the session cursor (polish.P25 #712, comp adjudication 3: the
// running shift is not an actual); cell tone is computed from `heatStyle`.

import type { SessionState } from '@/versions/v1/schema/scenario';
import type { SeriesTime } from '@/versions/v1/schema/reading-series';
import { diffMinutes, shiftOf } from '@/versions/v1/shared/clock.derive';
import { readingByIdLookup } from '@/versions/v1/shared/reading.derive';
import {
  formatShiftDateLabel,
  lastShiftStamps,
  shiftMillUtilAt,
  shiftTonnesAt,
  shiftValueAt,
  type DerivedShiftStamp,
} from './LandingCursor.transform';

export type ShiftHeatRow = {
  id: string;
  /** The shift's series stamp (its start). */
  at: SeriesTime;
  date: string;
  shift: 'D' | 'N';
  panel: 'A' | 'B' | 'C' | 'D';
  tonnes: number;
  pbRec: number;
  znRec: number;
  pbGrade: number;
  znGrade: number;
  millUtil: number;
};

/** True once the shift that starts at `at` has ended at the cursor. */
function shiftCompleteAt(at: SeriesTime, cursor: SeriesTime): boolean {
  return diffMinutes(shiftOf(at).end, cursor) <= 0;
}

/** The last `count` completed shift stamps, oldest first: the running shift at
 * the cursor (the 08 Jun day shift at 11:30) is never a column. */
export function completedShiftStamps(state: SessionState, count = 14): DerivedShiftStamp[] {
  return lastShiftStamps(state, count + 1)
    .filter((row) => shiftCompleteAt(row.at, state.cursor))
    .slice(-count);
}

export function shiftHeatRows(state: SessionState): readonly ShiftHeatRow[] {
  const stamps = completedShiftStamps(state, 14);
  return stamps.map((row, idx) => ({
    id: `sh-${idx + 1}`,
    at: row.at,
    date: formatShiftDateLabel(row.at),
    shift: row.kind === 'day' ? 'D' : 'N',
    panel: row.panel,
    tonnes: shiftTonnesAt(row.at, row.kind, state),
    pbRec: shiftValueAt('rs-pb-rec-shift', row.at, state) ?? 0,
    znRec: shiftValueAt('rs-zn-rec-shift', row.at, state) ?? 0,
    pbGrade: shiftValueAt('rs-pb-grade', row.at, state) ?? 0,
    znGrade: shiftValueAt('rs-zn-grade', row.at, state) ?? 0,
    millUtil: shiftMillUtilAt(row.at, state),
  }));
}

export type HeatTone = 'ok' | 'warn' | 'bad' | 'info';

/** Within tolerance = green; beyond 2× tolerance = red; otherwise amber. */
export function heatStyle(value: number, target: number, tolerance: number): HeatTone {
  if (value >= target) return 'ok';
  const gap = target - value;
  if (tolerance === 0) return gap > 0 ? 'bad' : 'ok';
  if (gap <= tolerance) return 'ok';
  if (gap <= 2 * tolerance) return 'warn';
  return 'bad';
}

export function heatTargets(_state: SessionState) {
  const zn = readingByIdLookup('rd-kpi-zn-rec');
  const pb = readingByIdLookup('rd-kpi-pb-rec');
  const ore = readingByIdLookup('rd-kpi-ore-td');
  const shiftTonnesTarget = Math.round((ore?.target ?? 1440) / 2);
  return {
    tonnes: { target: shiftTonnesTarget, tolerance: 20 },
    pbRec: { target: pb?.target ?? 75.0, tolerance: 0.5 },
    znRec: { target: zn?.target ?? 69.8, tolerance: 0.8 },
    pbGrade: { target: 62, tolerance: 0 },
    znGrade: { target: 55, tolerance: 0 },
    millUtil: {
      target: readingByIdLookup('rd-kpi-mill-util')?.target ?? 92,
      tolerance: 1.5,
    },
  };
}

export type ShiftSummaryRead = {
  nightAvgPct: number;
  dayAvgPct: number;
  best: ShiftHeatRow;
  /** Shifts at or above the Zn recovery target, and the window's shift count. */
  atTarget: number;
  shiftCount: number;
  znRecTarget: number;
};

/** The week's shift facts, derived from the 14 heat-map rows: nights vs days
 * (Zn recovery), the best shift and the count at the Zn recovery target. */
export function shiftSummaryRead(state: SessionState): ShiftSummaryRead {
  const rows = shiftHeatRows(state);
  const targets = heatTargets(state);
  const mean = (list: readonly ShiftHeatRow[]) => list.reduce((s, r) => s + r.znRec, 0) / list.length;
  const best = rows.reduce((bestRow, row) => (row.znRec > bestRow.znRec ? row : bestRow), rows[0]!);
  return {
    nightAvgPct: mean(rows.filter((r) => r.shift === 'N')),
    dayAvgPct: mean(rows.filter((r) => r.shift === 'D')),
    best,
    atTarget: rows.filter((r) => r.znRec >= targets.znRec.target).length,
    shiftCount: rows.length,
    znRecTarget: targets.znRec.target,
  };
}

/**
 * Panel-B night outlier insight — arithmetic from the 14 heat-map rows at cursor:
 * worstFive = 5 lowest znRec; panelBNightInWorst = count where panel==='B' && shift==='N';
 * panelBNightAvg vs otherAvg = mean znRec for Panel B Night vs all other rows.
 */
export function panelBNightPattern(state: SessionState): {
  panelBNightInWorst: number;
  /** How many lowest-recovery shifts the read ranks (five). */
  worstCount: number;
  panelBAvg: number;
  otherAvg: number;
} {
  const rows = shiftHeatRows(state);
  const sorted = [...rows].sort((a, b) => a.znRec - b.znRec);
  const worstFive = sorted.slice(0, 5);
  const panelBNightInWorst = worstFive.filter((row) => row.panel === 'B' && row.shift === 'N').length;
  const panelBNight = rows.filter((row) => row.panel === 'B' && row.shift === 'N');
  const other = rows.filter((row) => !(row.panel === 'B' && row.shift === 'N'));
  const panelBAvg = panelBNight.reduce((sum, row) => sum + row.znRec, 0) / panelBNight.length;
  const otherAvg = other.reduce((sum, row) => sum + row.znRec, 0) / other.length;
  return { panelBNightInWorst, worstCount: worstFive.length, panelBAvg, otherAvg };
}
